import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import { Input, InputGroup, List } from "rsuite";
import { useHistory } from "react-router-dom";
import SearchIcon from "@rsuite/icons/Search";
import FunnelIcon from "@rsuite/icons/Funnel";
import { colors } from "../../../../../ui/theme/colors";
import { shadows } from "../../../../../ui/theme/shadows";
import { Picker, Modal } from "../../../../../ui";
import { DateTime } from "luxon";

export const AlertsModal = ({
  open,
  onClose,
  alerts,
  systems,
  hhmSystems,
  currentView,
  recentValues,
}) => {
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [severityFilter, setSeverityFilter] = useState([]);
  const [systemFilter, setSystemFilter] = useState([]);
  const [alertTypeFilter, setAlertTypeFilter] = useState([]);
  const [customerFilter, setCustomerFilter] = useState([]);
  const [sortBy, setSortBy] = useState("newest");

  // Reset filters when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSeverityFilter([]);
      setSystemFilter([]);
      setAlertTypeFilter([]);
      setCustomerFilter([]);
      setSortBy("newest");
    }
  }, [open]);

  // Filter alerts based on current view
  const filterAlertsByView = (alertsList) => {
    if (!currentView) return alertsList;

    return alertsList.filter((alert) => {
      const pgTable = alert.pg_table?.toLowerCase() || "";

      if (currentView === "MMB") {
        // Mag Data view: show mag and edu alerts (exclude log/hhm)
        return !pgTable.includes("log") && !pgTable.includes("hhm");
      } else if (currentView === "EDU") {
        // ENV Data view: show only edu alerts
        return pgTable.includes("edu") || pgTable.includes("mmb_edu");
      } else if (currentView === "HHM") {
        // Log Data view: show only log/hhm alerts
        return pgTable.includes("log") || pgTable.includes("hhm");
      }

      return true;
    });
  };

  // Apply view filter to alerts
  const viewFilteredAlerts = useMemo(() => {
    return filterAlertsByView(alerts);
  }, [alerts, currentView]);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const severities = [
      ...new Set(viewFilteredAlerts.map((a) => a.severity)),
    ].sort();
    const systemIds = [
      ...new Set(viewFilteredAlerts.map((a) => a.system_id)),
    ].sort();
    const alertTypes = [
      ...new Set(viewFilteredAlerts.map((a) => a.field_name_alias)),
    ].sort();
    const customers = [
      ...new Set(
        viewFilteredAlerts.map((a) => a.customer_name).filter(Boolean),
      ),
    ].sort();

    return {
      severities: severities.map((s) => ({ label: s.toUpperCase(), value: s })),
      systems: systemIds.map((id) => {
        const system =
          systems.find((s) => s.id === id) ||
          hhmSystems.find((s) => s.id === id);
        return {
          label: system?.cus_sys_id || id,
          value: id,
        };
      }),
      alertTypes: alertTypes.map((type) => ({ label: type, value: type })),
      customers: customers.map((name) => ({ label: name, value: name })),
    };
  }, [viewFilteredAlerts, systems, hhmSystems]);

  // Filter and sort alerts
  const filteredAlerts = useMemo(() => {
    let filtered = [...viewFilteredAlerts];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((alert) => {
        const searchText =
          `${alert.system_id} ${alert.cus_sys_id} ${alert.site_name} ${alert.customer_name} ${alert.field_name_alias} ${alert.manufacturer}`.toLowerCase();
        return searchText.includes(query);
      });
    }

    // Severity filter
    if (severityFilter.length > 0) {
      filtered = filtered.filter((alert) =>
        severityFilter.includes(alert.severity),
      );
    }

    // System filter
    if (systemFilter.length > 0) {
      filtered = filtered.filter((alert) =>
        systemFilter.includes(alert.system_id),
      );
    }

    // Alert type filter
    if (alertTypeFilter.length > 0) {
      filtered = filtered.filter((alert) =>
        alertTypeFilter.includes(alert.field_name_alias),
      );
    }

    // Customer filter
    if (customerFilter.length > 0) {
      filtered = filtered.filter((alert) =>
        customerFilter.includes(alert.customer_name),
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.inserted_at) - new Date(a.inserted_at);
        case "oldest":
          return new Date(a.inserted_at) - new Date(b.inserted_at);
        case "severity":
          const severityOrder = { high: 3, medium: 2, low: 1 };
          return (
            (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0)
          );
        case "system":
          return a.system_id.localeCompare(b.system_id);
        default:
          return 0;
      }
    });

    return filtered;
  }, [
    viewFilteredAlerts,
    searchQuery,
    severityFilter,
    systemFilter,
    alertTypeFilter,
    customerFilter,
    sortBy,
  ]);

  const handleAlertClick = (alert) => {
    onClose();
    if (alert.pg_table.includes("log") || alert.pg_table.includes("hhm")) {
      history.push(`/system/hhm/${alert.system_id}`);
    } else {
      history.push(`/system/${alert.system_id}`);
    }
  };

  const getAlertMessage = (alert) => {
    if (alert.field_name_alias === "Compressor") {
      return `${alert.field_name_alias} is ${alert.threshold === "false" || alert.threshold === 0 ? "OFF" : "ON"}!`;
    } else if (
      alert.operator === "offline" ||
      alert.operator === "frozen_host"
    ) {
      return `${alert.field_name_alias} ${alert.threshold} ${alert.threshold_units}`;
    } else if (
      alert.field_name_alias?.toLowerCase() === "he level" &&
      alert.operator === "delta_neg"
    ) {
      return `${alert.field_name_alias} dropped ${alert.threshold} ${alert.threshold_units}`;
    } else if (
      alert.operator === "contains" ||
      alert.operator === "composite_equals"
    ) {
      return alert.field_name_alias;
    } else if (alert.operator === "absent_null_data") {
      return `${alert.field_name_alias}: ${alert.threshold} ${alert.threshold_units}`;
    } else {
      return `${alert.field_name_alias} is ${alert.operator.replace(/_/g, " ")} ${alert.threshold} ${alert.threshold_units}`;
    }
  };

  // Get raw current value from recentValues (for comparison)
  const getRawCurrentValue = (alert) => {
    if (!recentValues) return null;

    const systemData = recentValues.find(
      (system) => system.system_id === alert.system_id
    );

    if (!systemData) return null;

    const fieldAlias = alert.field_name_alias?.toLowerCase();

    if (fieldAlias === "compressor") {
      const value = systemData.comp_vib_status;
      if (value === undefined || value === null) return null;
      return value === "1" || value === 1 || value === true || value === "true" ? 1 : 0;
    } else if (fieldAlias === "he level" || fieldAlias?.includes("helium")) {
      if (systemData.helium_value === undefined || systemData.helium_value === null) return null;
      const system = systems?.find((s) => s.id === alert.system_id);
      if (system?.he_tank_volume && systemData.helium_units?.toUpperCase() === "LTRS") {
        return (systemData.helium_value / system.he_tank_volume) * 100;
      }
      return parseFloat(systemData.helium_value);
    } else if (fieldAlias === "he pressure" || fieldAlias?.includes("pressure") || fieldAlias?.includes("psia")) {
      if (systemData.pressure_value === undefined || systemData.pressure_value === null) return null;
      return parseFloat(systemData.pressure_value);
    }

    return null;
  };

  // Check if alert has resolved based on current value vs threshold
  const isAlertResolved = (alert) => {
    const currentValue = getRawCurrentValue(alert);
    if (currentValue === null) return false;

    const fieldAlias = alert.field_name_alias?.toLowerCase();
    const operator = alert.operator;

    // Can't determine resolution for delta alerts
    if (operator === "delta_neg" || operator === "delta_pos") return false;

    // Special handling for Compressor alerts
    // Alert triggers when compressor matches threshold state
    // Resolved when compressor is in the OPPOSITE state
    if (fieldAlias === "compressor") {
      const thresholdIsOff = alert.threshold === "false" || alert.threshold === "0" || alert.threshold === 0;
      const currentIsOff = currentValue === 0;
      // If alert was for compressor being OFF, resolved when it's ON (and vice versa)
      return thresholdIsOff ? !currentIsOff : currentIsOff;
    }

    const threshold = parseFloat(alert.threshold);
    if (isNaN(threshold)) return false;

    switch (operator) {
      case "less_than":
        return currentValue >= threshold;
      case "greater_than":
        return currentValue <= threshold;
      case "equals":
        return currentValue !== threshold;
      case "not_equals":
        return currentValue === threshold;
      default:
        return false;
    }
  };

  // Get current value from recentValues based on alert field
  const getCurrentValue = (alert) => {
    if (!recentValues) return null;

    // Don't show current value for delta/drop alerts
    if (alert.operator === "delta_neg" || alert.operator === "delta_pos") return null;

    const systemData = recentValues.find(
      (system) => system.system_id === alert.system_id
    );

    if (!systemData) return null;

    const fieldAlias = alert.field_name_alias?.toLowerCase();

    if (fieldAlias === "compressor") {
      const value = systemData.comp_vib_status;
      if (value === undefined || value === null) return null;
      // 0 = OFF, 1 = ON
      return value === "1" || value === 1 || value === true || value === "true"
        ? "ON"
        : "OFF";
    } else if (fieldAlias === "he level" || fieldAlias?.includes("helium")) {
      if (systemData.helium_value === undefined || systemData.helium_value === null) return null;
      // Check if we need to convert liters to percent
      const system = systems?.find((s) => s.id === alert.system_id);
      if (system?.he_tank_volume && systemData.helium_units?.toUpperCase() === "LTRS") {
        const percentValue = ((systemData.helium_value / system.he_tank_volume) * 100).toFixed(2);
        return `${percentValue} %`;
      }
      return `${parseFloat(systemData.helium_value).toFixed(2)} ${systemData.helium_units || alert.threshold_units || ""}`;
    } else if (fieldAlias === "he pressure" || fieldAlias?.includes("pressure") || fieldAlias?.includes("psia")) {
      if (systemData.pressure_value === undefined || systemData.pressure_value === null) return null;
      const decimals = systemData.pressure_units?.toLowerCase() === "mbar" ? 2 : 3;
      return `${parseFloat(systemData.pressure_value).toFixed(decimals)} ${systemData.pressure_units || alert.threshold_units || ""}`;
    }

    // Fallback to resolved_field_content if no match
    if (alert.resolved_field_content !== undefined && alert.resolved_field_content !== null) {
      return `${alert.resolved_field_content}${alert.resolved_field_units ? ` ${alert.resolved_field_units}` : ""}`;
    }

    return null;
  };

  const getCompressorCurrentValue = (alert) => {
    if (
      alert.field_name_alias === "Compressor" &&
      alert.resolved_field_content !== undefined
    ) {
      const value = alert.resolved_field_content;
      // 0 = OFF, 1 = ON
      return value === "1" || value === 1 || value === true || value === "true"
        ? "ON"
        : "OFF";
    }
    return null;
  };

  const formatTimestamp = (isoDate) => {
    const dt = DateTime.fromISO(isoDate);
    const formatted = dt.toFormat("MMM dd, 'at' HH:mm");
    const relative = dt.toRelative();
    return `${formatted} (${relative})`;
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case "high":
        return colors.error.main;
      case "medium":
        return colors.warning.main;
      case "low":
        return colors.neutral[600];
      default:
        return colors.primary.main;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xxl"
      title={`All Alerts (${viewFilteredAlerts.length})`}
      showFooter={false}
      showCloseButton={true}
    >
      <ModalContent>
        {/* Filters Section */}
        <FiltersSection>
          <SearchContainer>
            <StyledInputGroup inside>
              <StyledInput
                placeholder="Search by System ID, Site, Customer, Alert Type..."
                value={searchQuery}
                onChange={setSearchQuery}
              />
              <InputGroup.Addon>
                <SearchIcon style={{ color: colors.neutral[500] }} />
              </InputGroup.Addon>
            </StyledInputGroup>
          </SearchContainer>

          <FiltersGrid>
            <FilterGroup>
              <Picker
                onChange={setCustomerFilter}
                value={customerFilter}
                data={filterOptions.customers}
                multiple={true}
                searchable={true}
                placeholder="All Customers"
                width="100%"
              />
            </FilterGroup>

            <FilterGroup>
              <Picker
                onChange={setSeverityFilter}
                value={severityFilter}
                data={filterOptions.severities}
                multiple={true}
                searchable={false}
                placeholder="All Severities"
                width="100%"
              />
            </FilterGroup>

            <FilterGroup>
              <Picker
                onChange={setSystemFilter}
                value={systemFilter}
                data={filterOptions.systems}
                multiple={true}
                searchable={true}
                placeholder="All Systems"
                width="100%"
              />
            </FilterGroup>

            <FilterGroup>
              <Picker
                onChange={setAlertTypeFilter}
                value={alertTypeFilter}
                data={filterOptions.alertTypes}
                multiple={true}
                searchable={true}
                placeholder="All Types"
                width="100%"
              />
            </FilterGroup>

            <FilterGroup>
              <Picker
                label="Sort By"
                onChange={setSortBy}
                value={sortBy}
                data={[
                  { label: "Newest", value: "newest" },
                  { label: "Oldest", value: "oldest" },
                  { label: "Severity", value: "severity" },
                  { label: "System", value: "system" },
                ]}
                multiple={false}
                searchable={false}
                placeholder="Sort By"
                width="100%"
              />
            </FilterGroup>
          </FiltersGrid>

          <FilterSummary>
            Showing {filteredAlerts.length} of {viewFilteredAlerts.length}{" "}
            alerts
            {(severityFilter.length > 0 ||
              systemFilter.length > 0 ||
              alertTypeFilter.length > 0 ||
              customerFilter.length > 0 ||
              searchQuery) && (
              <ClearFilters
                onClick={() => {
                  setSearchQuery("");
                  setSeverityFilter([]);
                  setSystemFilter([]);
                  setAlertTypeFilter([]);
                  setCustomerFilter([]);
                }}
              >
                Clear All Filters
              </ClearFilters>
            )}
          </FilterSummary>
        </FiltersSection>

        {/* Alerts List */}
        <AlertsListContainer>
          {filteredAlerts.length > 0 ? (
            <List hover>
              {filteredAlerts.map((alert, index) => (
                <List.Item key={`${alert.system_id}-${alert.id}-${index}`}>
                  <AlertRow
                    severity={alert.severity}
                    resolved={isAlertResolved(alert)}
                    onClick={() => handleAlertClick(alert)}
                  >
                    <AlertSeverityBar severity={alert.severity} resolved={isAlertResolved(alert)} />
                    <AlertDetails>
                      <AlertSystem>
                        {alert.cus_sys_id || alert.system_id}
                        <AlertSite>
                          {alert.site_name && ` • ${alert.site_name}`}
                        </AlertSite>
                      </AlertSystem>
                      <AlertMessage>{getAlertMessage(alert)}</AlertMessage>
                    </AlertDetails>
                    {getCurrentValue(alert) && (
                      <AlertCurrentValueContainer>
                        <AlertCurrentValueLabel>
                          Current Value
                        </AlertCurrentValueLabel>
                        <AlertCurrentValue severity={alert.severity} resolved={isAlertResolved(alert)}>
                          {getCurrentValue(alert)}
                        </AlertCurrentValue>
                      </AlertCurrentValueContainer>
                    )}
                    <AlertTimeInfo>
                      <AlertTime>
                        Last: {formatTimestamp(alert.inserted_at)}
                      </AlertTime>
                      {alert.count > 1 && alert.min_inserted_at && (
                        <AlertTime>
                          First: {formatTimestamp(alert.min_inserted_at)}
                        </AlertTime>
                      )}
                    </AlertTimeInfo>
                    {alert.count > 1 && (
                      <AlertCountBadge>×{alert.count}</AlertCountBadge>
                    )}
                  </AlertRow>
                </List.Item>
              ))}
            </List>
          ) : (
            <NoResults>
              <FunnelIcon style={{ fontSize: "48px", opacity: 0.3 }} />
              <NoResultsText>No alerts match your filters</NoResultsText>
              <ClearFilters
                onClick={() => {
                  setSearchQuery("");
                  setSeverityFilter([]);
                  setSystemFilter([]);
                  setAlertTypeFilter([]);
                  setCustomerFilter([]);
                }}
              >
                Clear Filters
              </ClearFilters>
            </NoResults>
          )}
        </AlertsListContainer>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const FiltersSection = styled.div`
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  flex-shrink: 0;
  overflow: visible;
`;

const SearchContainer = styled.div`
  margin-bottom: 12px;
`;

const StyledInputGroup = styled(InputGroup)`
  width: 100%;
  border-radius: 25px;

  .rs-input-group-addon {
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-left: none;
    border-radius: 0 50px 50px 0;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 50px 0 0 50px !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  background: rgba(255, 255, 255, 0.8) !important;
  padding: 10px 20px !important;
  height: 42px !important;

  &:focus {
    border-color: ${colors.primary.main} !important;
    box-shadow: 0 0 0 2px rgba(44, 90, 160, 0.1) !important;
  }
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 4px;

  @media screen and (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: ${colors.neutral[600]};
  font-weight: 500;
`;

const ClearFilters = styled.button`
  background: none;
  border: none;
  color: ${colors.primary.main};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(44, 90, 160, 0.1);
  }
`;

const AlertsListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 20px 12px;
  min-height: 0; /* Important for flex scrolling */

  .rs-list {
    background: transparent;
  }

  .rs-list-item {
    padding: 0;
    margin-bottom: 6px;
    background: transparent;
    border: none;

    &:hover {
      background: transparent;
    }
  }
`;

const AlertRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto auto;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${(props) =>
    props.resolved
      ? "rgba(34, 197, 94, 0.15)"
      : "rgba(255, 255, 255, 0.5)"};
  border: 1px solid ${(props) =>
    props.resolved
      ? "rgba(34, 197, 94, 0.3)"
      : "rgba(255, 255, 255, 0.3)"};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${(props) =>
      props.resolved
        ? "rgba(34, 197, 94, 0.25)"
        : "rgba(255, 255, 255, 0.7)"};
    transform: translateX(4px);
    box-shadow: ${shadows.sm};
  }
`;

const AlertSeverityBar = styled.div`
  width: 4px;
  height: 32px;
  border-radius: 2px;
  flex-shrink: 0;
  background: ${(props) =>
    props.resolved
      ? colors.success.main
      : props.severity === "high"
        ? colors.error.main
        : props.severity === "medium"
          ? colors.warning.main
          : colors.neutral[500]};
`;

const AlertDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
`;

const AlertSystem = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.neutral[700]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AlertSite = styled.span`
  font-weight: 400;
  color: ${colors.neutral[600]};
  font-size: 12px;
`;

const AlertMessage = styled.div`
  font-size: 12px;
  color: ${colors.neutral[600]};
  line-height: 1.4;
`;

const AlertCurrentValueContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  pointer-events: none;
`;

const AlertCurrentValueLabel = styled.div`
  font-size: 9px;
  font-weight: 500;
  color: ${colors.neutral[500]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
`;

const AlertCurrentValue = styled.div`
  font-size: 18px;
  color: ${(props) => {
    if (props.resolved) return colors.success.main;
    switch (props.severity?.toLowerCase()) {
      case "high":
        return colors.error.main;
      case "medium":
        return colors.warning.main;
      case "low":
        return colors.neutral[600];
      default:
        return colors.primary.main;
    }
  }};
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
`;

const AlertTimeInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const AlertTime = styled.div`
  font-size: 11px;
  color: ${colors.neutral[500]};
  white-space: nowrap;
`;

const AlertCountBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: ${colors.primary.main};
  color: white;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
`;

const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: ${colors.neutral[500]};
`;

const NoResultsText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin: 16px 0;
  color: ${colors.neutral[600]};
`;
